import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { hideOthers } from 'aria-hidden'
import { callAllHandlers } from '@mote/helpers/assertion'
import { mergeRefs, useIds } from '@mote/helpers/hooks'
import { dialogManager, useDialogManager } from './DialogManager'
import type { PropGetter, UseDialogProps } from './types'

export function useDialog(props: UseDialogProps) {
  const {
    isOpen,
    onClose,
    id,
    maxWidth = 480,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    useInert = true,
    isCentered = !props.isDrawer,
    isDrawer = false,
    useAbsolutePosition = false,
    motionPreset = 'scale',
    scrollBehavior = 'outside',
    onOverlayClick: onOverlayClickProp,
    onEsc
  } = props

  const dialogRef = useRef<HTMLElement>(null)
  const overlayRef = useRef<HTMLElement>(null)
  const mouseDownTarget = useRef<EventTarget | null>(null)
  const [headerMounted, setHeaderMounted] = useState(false)
  const [contentMounted, setContentMounted] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  const [dialogId, headerId, contentId] = useIds(
    id,
    'mote-dialog',
    'mote-dialog--header',
    'mote-dialog--content'
  )

  /**
   * Hook used to polyfill `aria-modal` for older browsers.
   * It uses `aria-hidden` to all other nodes.
   *
   * @see https://developer.paciellogroup.com/blog/2018/06/the-current-state-of-modal-dialog-accessibility/
   */
  useAriaHidden(dialogRef, isOpen && useInert)

  useDialogManager(dialogRef, isOpen)

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
    }
    if (!isOpen && motionPreset === 'none') {
      setIsMounted(false)
    }
  }, [isOpen])

  const handleOnClose = useCallback(
    (origin?: string) => {
      onClose(origin)
    },
    [onClose]
  )

  const onMouseDown = useCallback((event: React.MouseEvent) => {
    mouseDownTarget.current = event.target
  }, [])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()

        if (closeOnEsc) {
          handleOnClose('escape')
        }

        onEsc?.()
      }
    },
    [closeOnEsc, handleOnClose, onEsc]
  )

  const getDialogProps: PropGetter = useCallback(
    (props = {}, ref = null) => ({
      role: 'dialog',
      ...props,
      ref: mergeRefs(ref, dialogRef),
      id: dialogId,
      tabIndex: -1,
      'aria-modal': true,
      'aria-labelledby': headerMounted ? headerId : undefined,
      'aria-describedby': contentMounted ? contentId : undefined,
      onClick: callAllHandlers(props.onClick, (event: React.MouseEvent) =>
        event.stopPropagation()
      )
    }),
    [
      contentId,
      contentMounted,
      dialogId,
      headerId,
      headerMounted,
      scrollBehavior
    ]
  )

  const onOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()

      if (mouseDownTarget.current !== event.target) return

      if (!dialogManager.isTopDialog(dialogRef)) return

      if (closeOnOverlayClick) {
        handleOnClose('overlay')
      }

      onOverlayClickProp?.()
    },
    [handleOnClose, closeOnOverlayClick, onOverlayClickProp]
  )

  const getDialogContainerProps: PropGetter = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      ref: mergeRefs(ref, overlayRef),
      onClick: callAllHandlers(props.onClick, onOverlayClick),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onMouseDown: callAllHandlers(props.onMouseDown, onMouseDown)
    }),
    [onKeyDown, onMouseDown, onOverlayClick]
  )

  const getDialogContentProps: PropGetter = useCallback(
    (props = {}) => ({
      ...props,
      scrollBehavior,
      id: contentId
    }),
    [scrollBehavior, contentId]
  )

  return {
    isOpen,
    onClose: handleOnClose,
    headerId,
    isDrawer,
    scrollBehavior,
    motionPreset,
    isCentered,
    useAbsolutePosition,
    maxWidth,
    isMounted,
    setIsMounted,
    setContentMounted,
    setHeaderMounted,
    dialogRef,
    overlayRef,
    getDialogProps,
    getDialogContainerProps,
    getDialogContentProps
  }
}

/**
 * Dialog hook to polyfill `aria-modal`.
 *
 * It applies `aria-hidden` to elements behind the dialog
 * to indicate that they're `inert`.
 *
 * @param ref React ref of the node
 * @param shouldHide whether `aria-hidden` should be applied
 */
export function useAriaHidden(
  ref: React.RefObject<HTMLElement>,
  shouldHide: boolean
) {
  const currentElement = ref.current

  useEffect(() => {
    if (!ref.current || !shouldHide) return undefined

    return hideOthers(ref.current)
  }, [shouldHide, ref, currentElement])
}
